/* eslint-disable no-debugger */
import { useEffect, useState } from 'react';

import validate from './NTAttachmentFormValidation';

import usePersonNationalTeamFileTypeData from '../../../state/personNationalTeamFileType/UsePersonNationalTeamFileTypeData';

import useForm from '@usas/ui-web/utils/UseForm';
import { formatValidFileTypes, getFileNameFromUrl, openFileInNewTab } from '@usas/ui-web/utils/FileUtils';
import { DEFAULT_ID } from '@usas/ui-web/utils/Constants';
import { getConfig } from '@usas/ui-web/config/RuntimeConfig';

const INITIAL_FORM_STATE = {
  fileTypeId: DEFAULT_ID,
  fileTypeName: '',
  isPublic: false,
  attachmentDescription: '',
  attachmentName: '',
  attachmentUrl: '',
  attachmentFile: {},
  uploadedAttachments: []
};

const INITIAL_FILE_TYPE_STATE = {
  hasTemplate: false,
  validFileTypes: [],
  validFileTypeNames: ''
};

const useNTAttachmentForm = (basePersonState, attachmentObj, onSubmitFormCallback, onIsDirtyChanged, onErrorCaught, uploadedAttachments) => {
  const BASE_UPLOAD_URL = getConfig().VITE_MEMBER_DOCS_NATIONAL_TEAM_ATTACHMENTS_BASE_UPLOAD_URL;
  const BASE_PUBLIC_UPLOAD_URL = getConfig().VITE_PUBLIC_DOCS_NATIONAL_TEAM_ATTACHMENTS_BASE_UPLOAD_URL;
  const { personNationalTeamFileTypeState, nationalTeamAttachmentTemplateState, getPersonNationalTeamAttachmentFileTypes,
    getNTAttachmentFileTemplate
  } = usePersonNationalTeamFileTypeData();
  const { formState, errorState, isDirty, handleSubmit, updateFormState, setFormData, setFormState,
    setErrors, setIsDirty
  } = useForm(INITIAL_FORM_STATE, onSubmitFormCallback, validate);
  const [fileTypeState, setFileTypeState] = useState(INITIAL_FILE_TYPE_STATE);

  const onDownloadFileTemplateClicked = async (event) => {
    try {
      event?.preventDefault();

      const getNTAttachmentFileTemplateResult = await getNTAttachmentFileTemplate(formState.fileTypeName);

      openFileInNewTab(getNTAttachmentFileTemplateResult.objData?.downloadUrl);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : setErrors({ formError: e?.message });
    }
  };

  const onFileLoad = (event) => {
    try {
      event?.preventDefault();

      setErrors({ ...errorState, attachmentFile: '' });

      if (event.target.files[0]) {
        const todayDate = new Date();
        const selectedFile = event.target.files[0];
        const mm = String(todayDate.getMonth() + 1).padStart(2, '0');
        const dd = String(todayDate.getDate()).padStart(2, '0');
        const yyyy = todayDate.getFullYear();
        const formattedDate = `${mm}${dd}${yyyy}`;

        const newFileName = `${formState.fileTypeName}_${formattedDate}_${basePersonState.objData.lastName}.jpg`;
        const renamedFile = new File([selectedFile], newFileName, { type: selectedFile.type });

        if (fileTypeState.validFileTypes.includes(renamedFile.type)) {
          setFormState({
            ...formState,
            attachmentFile: renamedFile,
            attachmentName: formState.attachmentName || renamedFile.name // if attachmentName is empty, set to file name
          });
          setIsDirty(true);
        } else {
          setErrors({ ...errorState, attachmentFile: `File type not permitted. Please provide a ${fileTypeState.validFileTypeNames} file.` });
        }
      }
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : setErrors({ formError: e?.message });
    }
  };

  const onFileUnload = (event) => {
    try {
      event?.preventDefault();

      setFormState({
        ...formState,
        attachmentFile: {},
        attachmentUrl: '',
        attachmentName: '',
        uploadedAttachments: []
      });
      setIsDirty(true);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : setErrors({ formError: e?.message });
    }
  };

  const onFileTypeChanged = (fileTypeId, fileTypeName) => {
    try {
      const targetFileType = personNationalTeamFileTypeState.arrayData.find((x) => x.personNationalAttachmentFileTypeId === fileTypeId);

      if (targetFileType) {
        const [validFileTypes, validFileTypeNames] = formatValidFileTypes(targetFileType.validFileTypes);

        if (formState.attachmentFile.size > 0 && validFileTypes.includes(formState.attachmentFile.type)) {
          setFormState({
            ...formState,
            isPublic: targetFileType.isPublicType === true,
            fileTypeId,
            fileTypeName
          });
        } else {
          setFormState({
            ...formState,
            isPublic: targetFileType.isPublicType === true,
            fileTypeId,
            fileTypeName,
            attachmentUrl: '',
            attachmentFile: {}
          });
        }

        setIsDirty(true);
        setFileTypeState({
          ...fileTypeState,
          hasTemplate: targetFileType.hasTemplate,
          validFileTypes,
          validFileTypeNames
        });
      }
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : setErrors({ formError: e?.message });
    }
  };

  useEffect(() => {
    try {
      if (!personNationalTeamFileTypeState.isArrayLoaded) {
        getPersonNationalTeamAttachmentFileTypes();
      } else {
        const targetFileType = attachmentObj
          ? personNationalTeamFileTypeState.arrayData.find((x) => x.personNationalAttachmentFileTypeId === attachmentObj.personNationalAttachmentFileTypeId)
          : personNationalTeamFileTypeState.arrayData[0];
        const [validFileTypes, validFileTypeNames] = formatValidFileTypes(targetFileType.validFileTypes);

        setFormData({
          ...INITIAL_FORM_STATE,
          fileTypeId: targetFileType.personNationalAttachmentFileTypeId,
          fileTypeName: targetFileType.typeName,
          isPublic: targetFileType.isPublicType === true,
          attachmentDescription: attachmentObj?.attachmentDescription || '',
          attachmentName: attachmentObj?.attachmentName || '',
          attachmentUrl: attachmentObj?.attachmentUrl || '',
          attachmentFile: {},
          uploadedAttachments: uploadedAttachments || []
        });

        setFileTypeState({
          ...fileTypeState,
          hasTemplate: targetFileType.hasTemplate,
          validFileTypes,
          validFileTypeNames
        });
      }
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : setErrors({ formError: e?.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentObj, personNationalTeamFileTypeState.isArrayLoaded]);

  useEffect(() => {
    try {
      if (onIsDirtyChanged) {
        onIsDirtyChanged(isDirty);
      }
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : setErrors({ formError: e?.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  return {
    isLoading: nationalTeamAttachmentTemplateState.isObjLoading,
    hasTemplate: fileTypeState.hasTemplate,
    validFileTypeNames: fileTypeState.validFileTypeNames,
    uploadedFileName: formState.attachmentFile?.name || getFileNameFromUrl(formState.attachmentUrl, formState.isPublic ? BASE_PUBLIC_UPLOAD_URL : BASE_UPLOAD_URL) || formState.attachmentName,
    isDirty,
    formState,
    errorState,
    onFileLoad,
    onFileUnload,
    handleSubmit,
    updateFormState,
    onFileTypeChanged,
    onDownloadFileTemplateClicked
  };
};

export default useNTAttachmentForm;